export const CHECKOUT_URL = 'https://financial-literacy-ready.impultienda.ar/checkout'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/x9PdsEZPa7AYK8tH1MS7Ni/205c4119-299f-4e4f-9051-a38ebf763c61.webp'

export const heroBadges: string[] = [
  '7-Day Money-Back Guarantee',
  'Complete 6-in-1 Teaching System',
  '36 Weeks Already Organized',
  'Ready-to-Teach Lessons & Activities',
  'Printable Worksheets & Assessments',
  'Answer Keys Included',
  'Middle & High School Ready',
]

export const noMoreLines: string[] = [
  'No more wondering what to teach next.',
  'No more building every worksheet from zero.',
  'No more searching ten different websites for activities.',
]

export const organizedItems: string[] = [
  'Lessons',
  'Visual explanations',
  'Worksheets',
  'Real-life scenarios',
  'Activities',
  'Assessments',
  'Answer keys',
  'Projects',
  'Yearly planning',
]

export const systemPills: string[] = [
  '6 Complete Digital Volumes',
  '36 Weeks of Organized Instruction',
  'Ready-to-Teach Lessons',
  'Printable Student Worksheets',
  'Real-Life Financial Activities',
  'Projects & Decision-Making Challenges',
  'Assessments & Answer Keys',
  'Full-Year Planning Tools',
  'Middle & High School Ready',
]

export interface ItemGroup {
  intro?: string
  items: string[]
  variant: 'list' | 'inline' | 'quotes'
}

export interface Volume {
  label: string
  title: string
  subtitle: string
  color: string
  description: string[]
  groups: ItemGroup[]
  afterText?: string[]
  footnote?: string
}

export const volumes: Volume[] = [
  {
    label: 'Volume 1',
    title: 'Money, Income & Banking',
    subtitle: 'Build the Financial Foundations Students Need',
    color: '#1d4ed8',
    description: [
      'Start the year with the essential concepts students need to understand how money works in everyday life.',
    ],
    groups: [
      {
        intro: 'Inside you’ll cover:',
        variant: 'list',
        items: [
          'money and financial decisions',
          'needs vs. wants',
          'trade-offs and opportunity cost',
          'income and different ways people get paid',
          'hourly pay and salary',
          'gross pay vs. net pay',
          'reading a paycheck',
          'banking basics',
          'checking and savings accounts',
          'debit cards and digital banking',
          'banking fees and account safety',
        ],
      },
    ],
    afterText: [
      'Every topic includes ready-to-use visual explanations, activities, worksheets, scenarios, quick checks, and teacher guidance.',
    ],
    footnote: 'Open the lesson and teach.',
  },
  {
    label: 'Volume 2',
    title: 'Budgeting & Smart Spending',
    subtitle: 'Teach Students How to Make Their Money Work',
    color: '#15803d',
    description: [
      'Help students understand where money goes and how everyday spending decisions affect their financial future.',
    ],
    groups: [
      {
        intro: 'Inside you’ll cover:',
        variant: 'list',
        items: [
          'building a budget',
          'income vs. expenses',
          'fixed and variable expenses',
          'needs vs. wants',
          'spending priorities',
          'comparison shopping',
          'recurring expenses',
          'financial goals',
          'smart purchasing decisions',
          'real-life budgeting scenarios',
        ],
      },
    ],
    afterText: [
      'Students don’t just learn what a budget is.',
      'They actually build one.',
    ],
    footnote: 'Less theory. More real-life application.',
  },
  {
    label: 'Volume 3',
    title: 'Credit, Debt & Borrowing',
    subtitle: 'Make Credit Easier to Understand and Easier to Teach',
    color: '#1e40af',
    description: [
      'Credit can quickly become confusing for students.',
      'This volume breaks it into clear, visual, practical lessons.',
    ],
    groups: [
      {
        intro: 'Inside you’ll cover:',
        variant: 'list',
        items: [
          'what credit is',
          'how borrowing works',
          'credit cards',
          'debt',
          'interest',
          'loans',
          'repayment',
          'responsible borrowing',
          'credit-related decisions',
          'common borrowing risks',
          'real-life scenarios',
        ],
      },
      {
        intro: 'Students learn to ask:',
        variant: 'quotes',
        items: [
          'Can I afford this?',
          'What will this really cost?',
          'What happens if I borrow?',
        ],
      },
    ],
  },
  {
    label: 'Volume 4',
    title: 'Saving & Investing',
    subtitle: 'Turn Future Money Concepts Into Something Students Can See',
    color: '#a16207',
    description: [
      'Teach students how saving today connects to future financial goals.',
    ],
    groups: [
      {
        intro: 'Inside you’ll cover:',
        variant: 'list',
        items: [
          'saving strategies',
          'financial goals',
          'emergency funds',
          'short-term vs. long-term goals',
          'compound growth',
          'introduction to investing',
          'risk and return',
          'diversification',
          'time horizon',
          'real-life saving decisions',
        ],
      },
    ],
    afterText: [
      'Visual activities and practical scenarios help students understand concepts that normally feel abstract.',
    ],
    footnote: 'Show it. Practice it. Apply it.',
  },
  {
    label: 'Volume 5',
    title: 'Taxes, Insurance & Real-Life Finance',
    subtitle: 'Prepare Students for the Money Decisions They’ll Actually Face',
    color: '#166534',
    description: [
      'Financial Literacy becomes powerful when students can connect the classroom to real life.',
    ],
    groups: [
      {
        intro: 'Inside you’ll cover:',
        variant: 'list',
        items: [
          'basic tax concepts',
          'why taxes exist',
          'insurance fundamentals',
          'managing financial risk',
          'housing expenses',
          'transportation costs',
          'utilities and recurring bills',
          'cost of living',
          'unexpected expenses',
          'financial protection',
          'real-life monthly money decisions',
        ],
      },
    ],
    afterText: [
      'Students begin to see that the price of something is rarely the only cost.',
    ],
    footnote: 'Teach the money skills they’ll actually use.',
  },
  {
    label: 'Volume 6',
    title: 'Projects, Assessments & Real-Life Application',
    subtitle: 'Bring the Entire Curriculum Together',
    color: '#1e3a8a',
    description: [
      'This volume helps students move from knowing financial concepts to actually using them.',
    ],
    groups: [
      {
        intro: 'Inside you’ll find:',
        variant: 'list',
        items: [
          'financial projects',
          'budgeting simulations',
          'real-life money scenarios',
          'decision-making challenges',
          'classroom activities',
          'review exercises',
          'unit assessments',
          'project rubrics',
          'financial application tasks',
          'end-of-year review',
        ],
      },
      {
        intro: 'Students must:',
        variant: 'inline',
        items: ['calculate', 'compare', 'prioritize', 'adapt', 'justify', 'decide'],
      },
    ],
    footnote:
      'Because Financial Literacy should never be only about memorizing definitions.',
  },
]

export const insideItems: string[] = [
  'Visual teaching pages',
  'Printable worksheets',
  'Paycheck examples',
  'Budget sheets',
  'Banking activities',
  'Transaction registers',
  'Financial decision cards',
  'Real-life scenarios',
  'Student challenges',
  'Quick checks',
  'Exit tickets',
  'Assessments',
  'Answer keys',
  'Teacher guidance',
  'Full-year organization',
]

export interface Bonus {
  label: string
  title: string
  tagline: string
  description: string
  highlights: string[]
  footnote: string
}

export const bonuses: Bonus[] = [
  {
    label: 'Bonus 1',
    title: 'The Financial Literacy Year Planner™',
    tagline: 'Your Entire 36-Week Curriculum Under Control',
    description:
      'A ready-to-use system to organize all 36 weeks, from yearly pacing to daily lessons.',
    highlights: ['Yearly pacing', 'Weekly & daily lessons', 'Student progress', 'Skills mastery'],
    footnote: 'Plan the year. Teach the week. Track the progress.',
  },
  {
    label: 'Bonus 2',
    title: '100 Financial Literacy Warm-Ups & Bell Ringers™',
    tagline: 'Never Wonder How to Start Class Again',
    description:
      '100 quick activities you can project or print in seconds — most take only a few minutes.',
    highlights: ['Would You Rather?', 'True or False', 'Calculate It', 'Spot the Mistake'],
    footnote: 'Open. Project. Discuss. Start the lesson.',
  },
  {
    label: 'Bonus 3',
    title: 'Real-Life Money Challenge Pack™',
    tagline: '30 Ready-to-Use Financial Decision Simulations',
    description:
      'Classroom-ready scenarios where students choose, calculate, prioritize and decide.',
    highlights: ['First paycheck', 'First apartment', 'First car', 'Monthly budgets'],
    footnote: 'Print the challenge and teach.',
  },
  {
    label: 'Bonus 4',
    title: 'Financial Literacy Assessment Pack™',
    tagline: 'Stop Creating Tests and Quizzes From Scratch',
    description:
      'A complete assessment system that measures more than memorization.',
    highlights: ['Quizzes & unit tests', 'Exit tickets', 'Project rubrics', 'Answer keys'],
    footnote: 'Assess. Identify. Reteach. Master.',
  },
  {
    label: 'Bonus 5',
    title: 'Financial Literacy Games & Classroom Activities™',
    tagline: 'Make Financial Literacy More Interactive',
    description:
      '30 ready-to-use games, team challenges, stations and simulations.',
    highlights: ['Budget Battles', 'Banking Bingo', 'Safe or Scam?', 'Expense Auctions'],
    footnote: 'Less lecturing. More learning by doing.',
  },
]

export interface Differentiator {
  title: string
  description: string[]
}

export const differentiators: Differentiator[] = [
  {
    title: 'Practical, Not Theoretical',
    description: [
      'Built around the financial decisions students will actually face.',
    ],
  },
  {
    title: 'Visual',
    description: [
      'Charts, worksheets, scenarios, cards, financial documents, comparison tables, and visual explanations.',
    ],
  },
  {
    title: 'Organized',
    description: [
      'Every volume has a purpose.',
      'Every week follows a progression.',
      'Every resource fits into the system.',
    ],
  },
  {
    title: 'Ready to Use',
    description: ['Print it.', 'Project it.', 'Assign it.', 'Teach it.'],
  },
  {
    title: 'Built for Teachers',
    description: [
      'You don’t need more random ideas.',
      'You need a system that removes the planning workload.',
    ],
  },
]

export const offerChecklist: string[] = [
  '6 Complete Digital Volumes',
  '36 Weeks of Organized Curriculum',
  'Ready-to-Teach Lessons',
  'Printable Worksheets',
  'Real-Life Activities',
  'Projects & Simulations',
  'Assessments & Answer Keys',
  'Full-Year Planning System',
  '5 Strategic Bonuses',
  'Instant Digital Access',
  'One-Time Payment',
]

export const offerAssurances: string[] = [
  'Instant Download',
  '7-Day Money-Back Guarantee',
  'No Monthly Fees',
  'Access From Computer or Tablet',
  'Print the Materials You Need',
]

export const differenceOneThings: string[] = [
  'one worksheet.',
  'Or one lesson.',
  'Or one unit.',
  'Or a random collection of activities.',
]

export const youKnowItems: string[] = [
  'what to teach',
  'what order to teach it in',
  'what activity to use',
  'what worksheet to print',
  'how students can practice',
  'how to assess understanding',
  'what answers to expect',
  'what comes next',
]

export const finalCtaPoints: string[] = [
  '6 Complete Volumes',
  '5 Strategic Bonuses',
  '36 Weeks of Curriculum',
  'Ready-to-Teach Lessons',
  'Worksheets & Real-Life Activities',
  'Assessments & Answer Keys',
  'Full-Year Planner',
  'Instant Access',
  'One-Time Payment',
]

export interface PurchaseNotice {
  name: string
  city: string
  minutesAgo: number
}

export const purchaseNotices: PurchaseNotice[] = [
  { name: 'Jessica M.', city: 'Austin, TX', minutesAgo: 3 },
  { name: 'Michael T.', city: 'Columbus, OH', minutesAgo: 8 },
  { name: 'Sarah L.', city: 'Denver, CO', minutesAgo: 14 },
  { name: 'David B.', city: 'Atlanta, GA', minutesAgo: 21 },
  { name: 'Emily R.', city: 'Phoenix, AZ', minutesAgo: 27 },
  { name: 'Chris P.', city: 'Charlotte, NC', minutesAgo: 35 },
  { name: 'Amanda K.', city: 'Portland, OR', minutesAgo: 42 },
  { name: 'Jason W.', city: 'Nashville, TN', minutesAgo: 51 },
]

export interface Faq {
  question: string
  answer: string[]
  bullets?: string[]
  closing?: string
}

export const faqs: Faq[] = [
  {
    question: 'What is FINANCIAL LITERACY READY™?',
    answer: [
      'FINANCIAL LITERACY READY™ is a complete digital Financial Literacy teaching system designed to help teachers organize and teach an entire school year without creating every lesson, activity, worksheet, and assessment from scratch.',
    ],
  },
  {
    question: 'Is this a physical product?',
    answer: [
      'No. FINANCIAL LITERACY READY™ is a digital resource.',
      'You receive access to the materials electronically so you can view, download, and print the resources you need.',
    ],
  },
  {
    question: 'Who is it designed for?',
    answer: [
      'It is primarily designed for Middle School and High School teachers teaching Financial Literacy, Personal Finance, Economics, Business Education, or related money-skills topics.',
    ],
  },
  {
    question: 'Is it suitable for new teachers?',
    answer: [
      'Yes.',
      'The structure is designed to reduce planning and give teachers a clear path to follow.',
      'You can open the appropriate section, review the material, and use the included activities and resources with your class.',
    ],
  },
  {
    question: 'Does it cover a full school year?',
    answer: [
      'Yes.',
      'FINANCIAL LITERACY READY™ is organized as a 36-week teaching system covering the major areas of Financial Literacy.',
    ],
  },
  {
    question: 'What topics are included?',
    answer: ['The system covers:'],
    bullets: [
      'Money',
      'Income',
      'Paychecks',
      'Banking',
      'Budgeting',
      'Smart Spending',
      'Credit',
      'Debt',
      'Borrowing',
      'Saving',
      'Investing',
      'Taxes',
      'Insurance',
      'Cost of Living',
      'Real-Life Financial Decisions',
      'Projects',
      'Assessments',
    ],
    closing: 'and more.',
  },
  {
    question: 'Can I print the materials?',
    answer: [
      'Yes.',
      'The worksheets, activities, assessments, planners, and classroom resources are designed to be used digitally or printed when appropriate.',
    ],
  },
  {
    question: 'Are answer keys included?',
    answer: [
      'Yes.',
      'Answer keys and teacher guidance are included throughout the system where objective answers are required.',
    ],
  },
  {
    question: 'Are there monthly fees?',
    answer: ['No.', 'You pay once and receive access to the product.'],
  },
  {
    question: 'How quickly do I receive access?',
    answer: ['Access is delivered digitally after purchase.'],
  },
  {
    question: 'Can I use the materials on a computer or tablet?',
    answer: [
      'Yes.',
      'The resources can be viewed digitally and printable materials can be downloaded for classroom use.',
    ],
  },
  {
    question: 'What if it isn’t right for me?',
    answer: [
      'Your purchase includes a 7-day money-back guarantee according to the applicable guarantee terms.',
    ],
  },
]
